import React, { useState, useEffect } from 'react';
import { Eye, RotateCw, Clock } from 'lucide-react';
import { Table, Space } from 'antd';

const RunningJobs = () => {
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [logs, setLogs] = useState('');

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`https://cloudify-prod.wizr.in/tasks/batch-jobs`);
      const data = await response.json();
      
      // Map tasks and add sequential numbers starting from 1
      const mappedTasks = data.tasks.map((task, index) => ({
        ...task,
        number: index + 1
      }));
      
      setTasks(mappedTasks);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const viewTaskLogs = (task) => {
    setSelectedTask(task);
    setLogs('Loading logs...');
    
    // Here you would fetch logs for the specific task
    // This is a placeholder - implement the actual log fetching logic
    setTimeout(() => {
      setLogs(`Logs for task ${task.taskId}...\nStarted at: ${task.startedAt}\nStatus: ${task.lastStatus}`);
    }, 500);
  };

  const columns = [
    {
      title: 'Task #',
      dataIndex: 'number',
      key: 'number',
    },
    {
      title: 'Status',
      dataIndex: 'lastStatus',
      key: 'lastStatus',
      render: (status) => {
        // Enhanced status badge styling
        const statusConfig = {
          RUNNING: {
            bgColor: 'bg-green-100',
            textColor: 'text-green-800',
            borderColor: 'border-green-200',
            icon: '✓'
          },
          STOPPED: {
            bgColor: 'bg-red-100',
            textColor: 'text-red-800', 
            borderColor: 'border-red-200',
            icon: '✗'
          },
          PENDING: {
            bgColor: 'bg-yellow-100',
            textColor: 'text-yellow-800',
            borderColor: 'border-yellow-200',
            icon: '⟳'
          },
          default: {
            bgColor: 'bg-blue-100',
            textColor: 'text-blue-800',
            borderColor: 'border-blue-200',
            icon: '•'
          }
        };
        
        const config = statusConfig[status] || statusConfig.default;
        
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor} border ${config.borderColor} flex items-center justify-center w-24`}>
            <span className="mr-1">{config.icon}</span> {status}
          </span>
        );
      },
    },
    {
      title: 'Started At',
      dataIndex: 'startedAt',
      key: 'startedAt',
      render: (timestamp) => (
        <div className="flex items-center text-gray-700">
          <Clock className="h-4 w-4 mr-2 text-gray-500" />
          {timestamp ? new Date(timestamp).toLocaleString() : 'N/A'}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, task) => (
        <Space>
          <button
            onClick={() => viewTaskLogs(task)}
            className="inline-flex items-center px-3 py-1.5 border border-indigo-500 text-indigo-600 bg-white rounded-md hover:bg-indigo-50 transition-all duration-200 shadow-sm text-sm font-medium"
          >
            <Eye className="h-4 w-4 mr-1.5 text-indigo-600" />
            View Logs
          </button>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900">ECS Tasks</h2>
        </div>
        <button
          onClick={fetchTasks}
          disabled={isLoading}
          className="inline-flex items-center text-sm px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-200 text-white rounded-lg transition-all duration-200 font-medium shadow-md disabled:opacity-70 disabled:cursor-not-allowed gap-2"
        >
          <RotateCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      
      <div className="rounded-lg overflow-hidden border border-gray-200">
        <Table
          columns={columns}
          dataSource={tasks}
          loading={isLoading}
          rowKey="taskId"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} tasks`,
          }}
        />
      </div>
      
      {/* Enhanced Logs Modal */}
      {selectedTask && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity"></div>
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl border border-gray-200">
              <div className="flex items-center justify-between p-5 border-b">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${
                    selectedTask.lastStatus === 'RUNNING' ? 'bg-green-500' :
                    selectedTask.lastStatus === 'STOPPED' ? 'bg-red-500' : 'bg-yellow-500'
                  }`}></div>
                  <h3 className="text-lg font-bold text-gray-800">
                    Task #{selectedTask.number} Logs
                  </h3>
                  <span className="text-sm text-gray-500">
                    {selectedTask.taskId}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedTask(null)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-5">
                <pre className="bg-gray-900 text-gray-200 p-5 rounded-lg overflow-auto h-96 font-mono text-sm leading-relaxed shadow-inner">
                  {logs}
                </pre>
              </div>
              <div className="flex justify-end p-4 border-t bg-gray-50 rounded-b-xl">
                <button
                  onClick={() => setSelectedTask(null)}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors mr-2"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunningJobs;